
import Vue, { PropType } from 'vue';
import { FeedbackReport } from '@run-diffusion/shared';
import { feedbackFileUrl } from './feedbackFiles';
import { bundleText, BundleFields, BundleTooLarge, readBundleFields } from './bundleText';
import { FeedbackBundle, FeedbackStep, parseBundle, relativeTime } from './feedbackReports';

// The last steps shown until "Show all": the moments just before the report.
const STEPS_SHOWN: number = 15;

/**
 * A feedback report's bundle: the board's flight recorder (web/board/src/feedback/recorder.ts),
 * read from the runnit-1 bucket as the report opens. Shows its errors, then its steps, each timed
 * against the moment the report was sent. The download link is the whole bundle, frame times,
 * board state and element snapshot included.
 */
export default Vue.extend({
	name: 'FeedbackActivity',
	props: {
		report: { type: Object as PropType<FeedbackReport>, required: true },
	},
	data () {
		return {
			STEPS_SHOWN,
			url: '',
			loading: false,
			error: '',
			bundle: null as FeedbackBundle | null,
			// when the board packed the bundle (its own clock, as the steps are)
			packedAt: 0,
			allSteps: false,
		};
	},
	created () {
		this.load();
	},
	computed: {
		stepsHeading (): string {
			if (!this.bundle) return '';
			if (!this.bundle.steps.length) return 'Steps';
			// a bundle with more steps than the page reads says so
			const of: string = this.bundle.stepCount > this.bundle.steps.length ? ` of ${this.bundle.stepCount}` : '';
			if (this.allSteps || this.bundle.steps.length <= STEPS_SHOWN) return of ? `The last ${this.bundle.steps.length}${of} steps, oldest first` : 'Steps, oldest first';
			return `The last ${STEPS_SHOWN}${of} steps`;
		},
		shownSteps (): FeedbackStep[] {
			if (!this.bundle) return [];
			return this.allSteps ? this.bundle.steps : this.bundle.steps.slice(-STEPS_SHOWN);
		},
	},
	methods: {
		relative (at: number): string {
			return relativeTime(at, this.packedAt);
		},
		async load () {
			if (!this.report.bundle) return;
			this.loading = true;
			this.error = '';
			try {
				this.url = this.url || await feedbackFileUrl(this.report.bundle.path);
			} catch (e) {
				console.error('[feedback] the bundle link failed to load', this.report.bundle.path, e);
				this.error = 'The activity could not be found.';
				this.loading = false;
				return;
			}
			try {
				const response: Response = await fetch(this.url);
				if (!response.ok) throw new Error(`HTTP ${response.status}`);
				// only the parts this shows: the board's snapshot in it is never parsed
				const fields: BundleFields = readBundleFields(await bundleText(await response.arrayBuffer(), this.report.bundle.gzip));
				this.packedAt = typeof fields.createdAt === 'number' ? fields.createdAt : this.report.createdAt.toMillis();
				this.bundle = parseBundle({ steps: fields.steps, errors: fields.errors });
			} catch (e) {
				console.error('[feedback] the bundle failed to read', this.report.bundle.path, e);
				this.error = e instanceof BundleTooLarge
					? 'This activity is too large to show here. Download the bundle to read it.'
					: 'The activity could not be read here. Download the bundle to read it.';
			} finally {
				this.loading = false;
			}
		},
	},
});
